import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { SEO } from "@/components/SEO"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import { InfoPopover } from "@/components/ui/info-popover"
import { ArrowDown, Wallet, Loader2, AlertCircle } from "lucide-react"
import welephantLogo from "@/assets/tokens/welephant.svg"
import stWelephantLogo from "@/assets/tokens/stwelephant.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { useTransactionToast } from "@/hooks/useTransactionToast"
import { formatUnits, parseUnits, Signer, Contract, InterfaceAbi, ContractTransactionResponse, Interface } from "ethers"
import { cn } from "@/lib/utils"
import { formatTokenAmount, formatUsdBigint } from "@/lib/ethersUtils"
import { StakingVaultABI } from "@/abi/StakingVaultABI"
import { WelephantSwapABI } from "@/abi/WelephantSwapABI"
import { STAKING_VAULT_ADDRESS, WELEPHANT_ADDRESS, SWAPPER_ADDRESS, GAME_CHAIN_ID, getMulticall3AddressForChain, GAS_LIMIT } from "@/lib/contracts"
import { ToggleableAmount } from "@/components/ui/toggleable-amount"
import { isUserRejection, getWalletErrorMessage, USER_REJECTED_MESSAGE, logContractError } from "@/lib/walletErrors"
import { validateBalance, isAmountExceedingBalance } from "@/lib/validateBalance"
import { createMulticallService } from "@/services/multicallService"
import { getChainProvider } from "@/lib/chainProvider"
import { isPageVisible } from "@/lib/pageVisibility"

// ERC20 ABI for WELEPHANT token
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
] as const

type StakeDirection = "STAKE" | "UNSTAKE"

interface APRData {
  day1: number
  day7: number
  day14: number
  day30: number
}

export function StakePage() {
  const { provider, signer, address, isConnected, balance } = useEthersContext()
  const { showTxPending, showTxError } = useTransactionToast()

  // Stake state
  const [direction, setDirection] = useState<StakeDirection>("STAKE")
  const [inputAmount, setInputAmount] = useState("")
  const [outputAmount, setOutputAmount] = useState("")
  const [amountPercent, setAmountPercent] = useState(0)
  const isUpdatingFromSlider = useRef(false)

  // APR state
  const [aprData, setAprData] = useState<APRData>({ day1: 0, day7: 0, day14: 0, day30: 0 })

  // Loading states
  const [isEstimating, setIsEstimating] = useState(false)
  const [isStaking, setIsStaking] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // USD estimates
  const [inputUsd, setInputUsd] = useState<string>("$0.00")
  const [outputUsd, setOutputUsd] = useState<string>("$0.00")

  // Vault stats USD values
  const [totalStakedUsd, setTotalStakedUsd] = useState<string | null>(null)
  const [userPositionUsd, setUserPositionUsd] = useState<string | null>(null)
  const [exchangeRateUsd, setExchangeRateUsd] = useState<string | null>(null)

  // Balance USD values (for ToggleableAmount)
  const [welephantBalanceUsd, setWelephantBalanceUsd] = useState<string | null>(null)

  // Balances
  const [welephantBalance, setWelephantBalance] = useState<bigint>(0n)
  const [stWelephantBalance, setStWelephantBalance] = useState<bigint>(0n)

  // Vault stats
  const [totalAssets, setTotalAssets] = useState<bigint>(0n)
  const [totalSupply, setTotalSupply] = useState<bigint>(0n)

  // Allowance for WELEPHANT
  const [allowance, setAllowance] = useState<bigint>(0n)

  // Token decimals (fetched from contracts, defaults match expected values)
  // WELEPHANT: 9 decimals (matches mainnet)
  // stWELEPHANT: 18 decimals (9 + 9 vault offset)
  const [welephantDecimals, setWelephantDecimals] = useState<number>(9)
  const [stWelephantDecimals, setStWelephantDecimals] = useState<number>(18)

  // Contracts
  const stakingContract = useMemo(() => {
    if (!provider) return null
    return new Contract(STAKING_VAULT_ADDRESS, StakingVaultABI as InterfaceAbi, provider)
  }, [provider])

  const welephantContract = useMemo(() => {
    if (!provider) return null
    return new Contract(WELEPHANT_ADDRESS, ERC20_ABI as InterfaceAbi, provider)
  }, [provider])

  const swapContract = useMemo(() => {
    if (!provider) return null
    return new Contract(SWAPPER_ADDRESS, WelephantSwapABI as InterfaceAbi, provider)
  }, [provider])

  // Fetch decimals from contracts using Multicall3
  useEffect(() => {
    const fetchDecimals = async () => {
      if (!welephantContract || !stakingContract) return

      try {
        const chainProvider = await getChainProvider(GAME_CHAIN_ID)
        const multicall = createMulticallService(chainProvider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

        const erc20Iface = new Interface(ERC20_ABI)
        const vaultIface = new Interface(StakingVaultABI)

        const calls = [
          multicall.encodeCall(WELEPHANT_ADDRESS, erc20Iface, 'decimals'),
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'decimals'),
        ]

        const [welDecimals, stWelDecimals] = await multicall.callAndDecode<[bigint, bigint]>(calls)
        setWelephantDecimals(Number(welDecimals))
        setStWelephantDecimals(Number(stWelDecimals))
      } catch (err) {
        console.error("Failed to fetch decimals:", err)
      }
    }

    fetchDecimals()
  }, [welephantContract, stakingContract])

  // Get decimals for current direction
  const inputDecimals = direction === "STAKE" ? welephantDecimals : stWelephantDecimals
  const outputDecimals = direction === "STAKE" ? stWelephantDecimals : welephantDecimals

  // Fetch balances and vault stats using Multicall3
  useEffect(() => {
    const fetchBalances = async () => {
      if (!isPageVisible()) return
      if (!provider || !welephantContract || !stakingContract) return

      try {
        const chainProvider = await getChainProvider(GAME_CHAIN_ID)
        const multicall = createMulticallService(chainProvider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

        const erc20Iface = new Interface(ERC20_ABI)
        const vaultIface = new Interface(StakingVaultABI)

        // Build calls - vault stats always, user balances if connected
        const calls = [
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'totalAssets'),
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'totalSupply'),
        ]

        if (address) {
          calls.push(
            multicall.encodeCall(WELEPHANT_ADDRESS, erc20Iface, 'balanceOf', [address]),
            multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'balanceOf', [address]),
          )
        }

        const results = await multicall.callAndDecode<[bigint, bigint, bigint?, bigint?]>(calls)

        setTotalAssets(results[0])
        setTotalSupply(results[1])

        if (address && results[2] !== undefined && results[3] !== undefined) {
          setWelephantBalance(results[2])
          setStWelephantBalance(results[3])
        }
      } catch (err) {
        console.error("Failed to fetch balances:", err)
      }
    }

    fetchBalances()
    const interval = setInterval(fetchBalances, 10000)
    return () => clearInterval(interval)
  }, [provider, address, welephantContract, stakingContract])

  // Check allowance for staking
  useEffect(() => {
    const checkAllowance = async () => {
      if (!welephantContract || !address || direction !== "STAKE") return

      try {
        const currentAllowance = await welephantContract.allowance(address, STAKING_VAULT_ADDRESS)
        setAllowance(currentAllowance)
      } catch (err) {
        console.error("Failed to check allowance:", err)
      }
    }

    checkAllowance()
  }, [welephantContract, address, direction])

  // Fetch APR data using Multicall3
  useEffect(() => {
    const fetchAPR = async () => {
      if (!isPageVisible()) return
      if (!stakingContract) return

      try {
        const chainProvider = await getChainProvider(GAME_CHAIN_ID)
        const multicall = createMulticallService(chainProvider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

        const vaultIface = new Interface(StakingVaultABI)

        // Use allowFailure for APR calls since they may fail for new vaults
        const calls = [
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getAPR', [1], true),
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getAPR', [7], true),
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getAPR', [14], true),
          multicall.encodeCall(STAKING_VAULT_ADDRESS, vaultIface, 'getAPR', [30], true),
        ]

        const results = await multicall.callAndDecode<[bigint | null, bigint | null, bigint | null, bigint | null]>(calls)

        setAprData({
          day1: Number(results[0] ?? 0n) / 100, // Convert basis points to percentage
          day7: Number(results[1] ?? 0n) / 100,
          day14: Number(results[2] ?? 0n) / 100,
          day30: Number(results[3] ?? 0n) / 100,
        })
      } catch (err) {
        console.error("Failed to fetch APR:", err)
      }
    }

    fetchAPR()
    const interval = setInterval(fetchAPR, 60000) // Refresh APR every minute
    return () => clearInterval(interval)
  }, [stakingContract])

  // Calculate USD values for vault stats
  useEffect(() => {
    const calculateUsdValues = async () => {
      if (!isPageVisible()) return
      if (!swapContract || !stakingContract) return

      try {
        // Get WELEPHANT price once (handles unwrap calculation internally)
        const pricePerToken: bigint = await swapContract.getWelephantPriceInUSD()
        const welDivisor = BigInt(10 ** welephantDecimals)

        // Calculate exchange rate USD (value of 1 stWELEPHANT in USD)
        const oneStWelephant = parseUnits("1", stWelephantDecimals)
        const welephantForOne: bigint = await stakingContract.previewRedeem(oneStWelephant)
        if (welephantForOne > 0n) {
          const usdValue = pricePerToken * welephantForOne / welDivisor
          setExchangeRateUsd(formatUsdBigint(usdValue))
        } else {
          setExchangeRateUsd(null)
        }

        // Calculate total staked USD
        if (totalAssets > 0n) {
          const usdValue = pricePerToken * totalAssets / welDivisor
          setTotalStakedUsd(formatUsdBigint(usdValue))
        } else {
          setTotalStakedUsd("0.00")
        }

        // Calculate user position USD (based on underlying WELEPHANT value)
        if (stWelephantBalance > 0n) {
          const underlyingAssets: bigint = await stakingContract.previewRedeem(stWelephantBalance)
          const usdValue = pricePerToken * underlyingAssets / welDivisor
          setUserPositionUsd(formatUsdBigint(usdValue))
        } else {
          setUserPositionUsd(null)
        }

        // Calculate WELEPHANT wallet balance USD
        if (welephantBalance > 0n) {
          const usdValue = pricePerToken * welephantBalance / welDivisor
          setWelephantBalanceUsd(formatUsdBigint(usdValue))
        } else {
          setWelephantBalanceUsd(null)
        }
      } catch (err) {
        console.error("Failed to calculate vault USD values:", err)
      }
    }

    calculateUsdValues()
    const interval = setInterval(calculateUsdValues, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [swapContract, stakingContract, totalAssets, stWelephantBalance, stWelephantDecimals, welephantBalance])

  // Get max input balance
  const maxInputBalance = direction === "STAKE" ? welephantBalance : stWelephantBalance

  // Update slider when input changes manually (not from slider)
  useEffect(() => {
    if (isUpdatingFromSlider.current) {
      isUpdatingFromSlider.current = false
      return
    }
    if (!inputAmount || maxInputBalance === 0n) {
      setAmountPercent(0)
      return
    }
    try {
      const inputWei = parseUnits(inputAmount, inputDecimals)
      const percent = Number((inputWei * 100n) / maxInputBalance)
      setAmountPercent(Math.min(100, Math.max(0, percent)))
    } catch {
      // Invalid input, ignore
    }
  }, [inputAmount, maxInputBalance, inputDecimals])

  // Reset amount percent when direction changes
  useEffect(() => {
    setAmountPercent(0)
  }, [direction])

  // Handle slider change
  const handleAmountSliderChange = useCallback((percent: number) => {
    setAmountPercent(percent)
    if (maxInputBalance === 0n) {
      setInputAmount("")
      return
    }
    isUpdatingFromSlider.current = true
    const amountWei = (maxInputBalance * BigInt(percent)) / 100n
    setInputAmount(trimDecimals(formatUnits(amountWei, inputDecimals), 5))
  }, [maxInputBalance, inputDecimals])

  // Estimate output amount and USD values
  const estimateOutput = useCallback(async () => {
    if (!stakingContract || !inputAmount || parseFloat(inputAmount) <= 0) {
      setOutputAmount("")
      setInputUsd("$0.00")
      setOutputUsd("$0.00")
      return
    }

    setIsEstimating(true)
    setError(null)

    try {
      const inputWei = parseUnits(inputAmount, inputDecimals)
      let welephantAmount: bigint

      if (direction === "STAKE") {
        // Preview how many shares we'll get for depositing assets
        const shares = await stakingContract.previewDeposit(inputWei)
        setOutputAmount(formatTokenAmount(shares, outputDecimals))
        // Input is WELEPHANT
        welephantAmount = inputWei
      } else {
        // Preview how many assets we'll get for redeeming shares
        const assets = await stakingContract.previewRedeem(inputWei)
        setOutputAmount(formatTokenAmount(assets, outputDecimals))
        // Output is WELEPHANT, use that for USD calculation
        welephantAmount = assets
      }

      // Calculate USD value using WELEPHANT price
      if (swapContract && welephantAmount > 0n) {
        try {
          const pricePerToken: bigint = await swapContract.getWelephantPriceInUSD()
          const usdValue = pricePerToken * welephantAmount / BigInt(10 ** welephantDecimals)
          const formattedUsd = formatUsdBigint(usdValue)
          setInputUsd(`$${formattedUsd}`)
          setOutputUsd(`$${formattedUsd}`) // Same value, just converting form
        } catch (err) {
          console.error("Failed to estimate USD:", err)
          setInputUsd("$—")
          setOutputUsd("$—")
        }
      }
    } catch (err) {
      console.error("Failed to estimate:", err)
      setError("Failed to get estimate")
    } finally {
      setIsEstimating(false)
    }
  }, [stakingContract, swapContract, inputAmount, direction, inputDecimals, outputDecimals])

  // Debounced estimation
  useEffect(() => {
    const timer = setTimeout(estimateOutput, 300)
    return () => clearTimeout(timer)
  }, [estimateOutput])

  // Toggle stake direction
  const toggleDirection = () => {
    setDirection(d => d === "STAKE" ? "UNSTAKE" : "STAKE")
    setInputAmount("")
    setOutputAmount("")
    setError(null)
  }

  // Set max input
  const setMaxInput = () => {
    setInputAmount(formatUnits(maxInputBalance, inputDecimals))
  }

  // Approve WELEPHANT spending
  const handleApprove = async () => {
    if (!signer || !welephantContract) return

    // Pre-flight gas check
    const validation = validateBalance({ balance })
    if (!validation.valid) {
      showTxError("Approve", validation.error!)
      return
    }

    setIsApproving(true)
    setError(null)

    try {
      const inputWei = parseUnits(inputAmount, welephantDecimals)
      const connectedContract = welephantContract.connect(signer as Signer) as Contract & {
        approve(spender: string, amount: bigint): Promise<ContractTransactionResponse>
      }
      const tx = await connectedContract.approve(STAKING_VAULT_ADDRESS, inputWei, { gasLimit: GAS_LIMIT })
      const txToast = showTxPending("Approve", tx.hash)
      await tx.wait()
      txToast.success()
      setAllowance(inputWei)
    } catch (err: unknown) {
      logContractError("Stake.approve", err)
      if (isUserRejection(err)) {
        showTxError("Approve", USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Approval failed")
        showTxError("Approve", errorMessage || "Approval failed")
        setError(errorMessage)
      }
    } finally {
      setIsApproving(false)
    }
  }

  // Execute stake/unstake
  const handleStake = async () => {
    if (!stakingContract || !signer || !address || !inputAmount) return

    // Pre-flight balance check (gas + token)
    const inputWei = parseUnits(inputAmount, inputDecimals)
    const validation = validateBalance({
      balance,
      tokenBalance: inputToken.balance,
      tokenAmount: inputWei,
      tokenSymbol: inputToken.symbol,
      tokenDecimals: inputToken.decimals,
    })
    if (!validation.valid) {
      showTxError(direction === "STAKE" ? "Stake" : "Unstake", validation.error!)
      return
    }

    setIsStaking(true)
    setError(null)

    try {
      const connectedContract = stakingContract.connect(signer as Signer) as Contract & {
        deposit(assets: bigint, receiver: string): Promise<ContractTransactionResponse>
        redeem(shares: bigint, receiver: string, owner: string): Promise<ContractTransactionResponse>
      }

      if (direction === "STAKE") {
        const tx = await connectedContract.deposit(inputWei, address, { gasLimit: GAS_LIMIT })
        const txToast = showTxPending("Stake", tx.hash)
        await tx.wait()
        txToast.success()
      } else {
        const tx = await connectedContract.redeem(inputWei, address, address, { gasLimit: GAS_LIMIT })
        const txToast = showTxPending("Unstake", tx.hash)
        await tx.wait()
        txToast.success()
      }

      // Clear form
      setInputAmount("")
      setOutputAmount("")
    } catch (err: unknown) {
      const action = direction === "STAKE" ? "Stake" : "Unstake"
      logContractError(`Stake.${action.toLowerCase()}`, err)
      if (isUserRejection(err)) {
        showTxError(action, USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Transaction failed")
        showTxError(action, errorMessage || "Transaction failed")
        setError(errorMessage)
      }
    } finally {
      setIsStaking(false)
    }
  }

  // Check if approval is needed
  const needsApproval = useMemo(() => {
    if (direction !== "STAKE" || !inputAmount || parseFloat(inputAmount) <= 0) {
      return false
    }
    try {
      return parseUnits(inputAmount, welephantDecimals) > allowance
    } catch {
      return false
    }
  }, [direction, inputAmount, welephantDecimals, allowance])

  // Check if stake is possible
  const canStake = isConnected &&
    inputAmount &&
    parseFloat(inputAmount) > 0 &&
    !isEstimating &&
    !isStaking &&
    !needsApproval

  // Get current input/output token info
  const inputToken = direction === "STAKE"
    ? { symbol: "WELEPHANT", balance: welephantBalance, decimals: welephantDecimals, balanceUsd: welephantBalanceUsd }
    : { symbol: "stWELEPHANT", balance: stWelephantBalance, decimals: stWelephantDecimals, balanceUsd: userPositionUsd }

  const outputToken = direction === "STAKE"
    ? { symbol: "stWELEPHANT", balance: stWelephantBalance, decimals: stWelephantDecimals, balanceUsd: userPositionUsd }
    : { symbol: "WELEPHANT", balance: welephantBalance, decimals: welephantDecimals, balanceUsd: welephantBalanceUsd }

  // Calculate exchange rate: how much WELEPHANT per 1 stWELEPHANT
  // If totalSupply is 0, rate is 1:1
  const exchangeRate = useMemo(() => {
    if (totalSupply === 0n) return 1
    // totalAssets is in WELEPHANT (9 decimals), totalSupply is in stWELEPHANT (18 decimals)
    // To get WELEPHANT per stWELEPHANT: (totalAssets / 10^9) / (totalSupply / 10^18)
    // = totalAssets * 10^18 / (totalSupply * 10^9) = totalAssets * 10^9 / totalSupply
    const scaledAssets = totalAssets * BigInt(10 ** 9)
    return Number(scaledAssets) / Number(totalSupply)
  }, [totalAssets, totalSupply])

  // Calculate user's percentage of the vault
  const userVaultPercentage = useMemo(() => {
    if (totalSupply === 0n || stWelephantBalance === 0n) return 0
    return Number(stWelephantBalance * 10000n / totalSupply) / 100
  }, [stWelephantBalance, totalSupply])

  return (
    <>
      <SEO
        title="Stake"
        url="/stake"
        description="Stake WELEPHANT tokens to earn stWELEPHANT rewards. Earn passive income from prediction mining game fees with competitive APR."
        keywords="WELEPHANT staking, stWELEPHANT, crypto staking, passive income, DeFi yield"
      />
      <div className="container py-16">
      <div className="mx-auto max-w-md">
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Stake</h2>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="font-medium text-primary">{aprData.day1.toFixed(2)}% APR</span>
              <InfoPopover side="bottom" contentClassName="min-w-[140px]">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">24h APR:</span>
                    <span className="font-medium">{aprData.day1.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">7d APR:</span>
                    <span className="font-medium">{aprData.day7.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">14d APR:</span>
                    <span className="font-medium">{aprData.day14.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">30d APR:</span>
                    <span className="font-medium">{aprData.day30.toFixed(2)}%</span>
                  </div>
                </div>
              </InfoPopover>
            </div>
          </div>

          <CardContent className="p-4 space-y-2">
            {/* From Token */}
            <div className={cn("rounded-xl bg-muted/50 p-4", isAmountExceedingBalance(inputAmount, inputToken.balance, inputToken.decimals) && "bg-destructive/10 border border-destructive/30")}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-muted-foreground">From</span>
                <TokenButton symbol={inputToken.symbol} />
              </div>

              <div className="flex items-center justify-end gap-2 mb-3">
                <Slider
                  value={[amountPercent]}
                  onValueChange={([value]) => handleAmountSliderChange(value)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-24"
                />
                <span className="text-sm text-primary w-10 text-right">{amountPercent}%</span>
              </div>

              <div className="mb-2">
                <Input
                  type="number"
                  placeholder="0"
                  value={inputAmount}
                  onChange={(e) => setInputAmount(e.target.value)}
                  className="text-2xl !border-0 bg-transparent p-0 h-auto text-right"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={setMaxInput}
                  className={cn("flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors", isAmountExceedingBalance(inputAmount, inputToken.balance, inputToken.decimals) && "text-destructive")}
                >
                  <Wallet className="h-3 w-3" />
                  <span onClick={(e) => e.stopPropagation()}>
                    <ToggleableAmount
                      amount={formatTokenAmount(inputToken.balance, inputToken.decimals)}
                      symbol={inputToken.symbol}
                      usdValue={inputToken.balanceUsd}

                    />
                  </span>
                </button>
                <span className="text-sm text-muted-foreground">{inputUsd}</span>
              </div>
            </div>

            {/* Swap Direction Button */}
            <div className="flex justify-center -my-1 relative z-10">
              <button
                onClick={toggleDirection}
                className="rounded-full border-4 border-background bg-muted p-2 hover:bg-muted/80 transition-colors"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>

            {/* To Token */}
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-muted-foreground">To</span>
                <TokenButton symbol={outputToken.symbol} />
              </div>

              <div className={cn(
                "text-2xl font-medium mb-2 text-right",
                isEstimating && "animate-pulse"
              )}>
                {isEstimating ? (
                  <Loader2 className="h-6 w-6 animate-spin ml-auto" />
                ) : (
                  outputAmount || "0"
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Wallet className="h-3 w-3" />
                  <ToggleableAmount
                    amount={formatTokenAmount(outputToken.balance, outputToken.decimals)}
                    symbol={outputToken.symbol}
                    usdValue={outputToken.balanceUsd}
                  />
                </span>
                <span className="text-sm text-muted-foreground">{outputUsd}</span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm p-3 rounded-lg bg-destructive/10">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Action Button */}
            {!isConnected ? (
              <Button disabled className="w-full h-14 text-lg">
                Connect Wallet
              </Button>
            ) : needsApproval ? (
              <Button
                onClick={handleApprove}
                disabled={isApproving}
                className="w-full h-14 text-lg"
              >
                {isApproving ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Approving...
                  </>
                ) : (
                  `Approve ${inputToken.symbol}`
                )}
              </Button>
            ) : (
              <Button
                onClick={handleStake}
                disabled={!canStake}
                className="w-full h-14 text-lg"
              >
                {isStaking ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {direction === "STAKE" ? "Staking..." : "Unstaking..."}
                  </>
                ) : (
                  direction === "STAKE" ? "Stake" : "Unstake"
                )}
              </Button>
            )}

          </CardContent>
        </Card>

        {/* Vault Stats */}
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {/* User Position - only show if connected and has balance */}
              {isConnected && stWelephantBalance > 0n && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Your Position</span>
                    <ToggleableAmount
                      amount={formatTokenAmount(stWelephantBalance, stWelephantDecimals)}
                      symbol="stWELEPHANT"
                      usdValue={userPositionUsd}
                      amountClassName="text-sm font-medium"
                      labelClassName="text-sm"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Pool Share</span>
                    <span className="text-sm font-medium">
                      {userVaultPercentage < 0.01 && userVaultPercentage > 0
                        ? '<0.01'
                        : userVaultPercentage.toFixed(2)}%
                    </span>
                  </div>
                </>
              )}

              {/* Exchange Rate */}
              <div className={isConnected && stWelephantBalance > 0n ? "border-t pt-3 mt-3 space-y-3" : "space-y-3"}>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Exchange Rate</span>
                  <span className="text-sm font-medium">
                    1 stWELEPHANT ={' '}
                    <ToggleableAmount
                      amount={formatExchangeRate(exchangeRate)}
                      symbol="WELEPHANT"
                      usdValue={exchangeRateUsd}
                      amountClassName="font-medium"
                      labelClassName=""
                    />
                  </span>
                </div>

                {/* Total Staked */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Staked</span>
                  <ToggleableAmount
                    amount={formatTokenAmount(totalAssets, welephantDecimals)}
                    symbol="WELEPHANT"
                    usdValue={totalStakedUsd}
                    amountClassName="text-sm font-medium"
                    labelClassName="text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  )
}

// Token button component
function TokenButton({ symbol }: { symbol: string }) {
  const logo = symbol === "WELEPHANT" ? welephantLogo : stWelephantLogo

  return (
    <div className="flex items-center gap-2">
      <img src={logo} alt={symbol} className="w-6 h-6" />
      <span className="font-medium">{symbol}</span>
    </div>
  )
}

// Helper functions

/** Trim a formatUnits string to at most `maxDecimals` decimal places, stripping trailing zeros */
function trimDecimals(value: string, maxDecimals: number): string {
  const dotIndex = value.indexOf('.')
  if (dotIndex === -1) return value
  const decimals = value.slice(dotIndex + 1)
  if (decimals.length <= maxDecimals) return value.replace(/\.?0+$/, '') || value.slice(0, dotIndex)
  const trimmed = value.slice(0, dotIndex + 1 + maxDecimals)
  return trimmed.replace(/\.?0+$/, '') || value.slice(0, dotIndex)
}

function formatExchangeRate(rate: number): string {
  if (!isFinite(rate) || isNaN(rate)) return "—"
  if (rate === 0) return "0"
  if (rate >= 1000000000) return (rate / 1000000000).toFixed(2) + "B"
  if (rate >= 1000000) return (rate / 1000000).toFixed(2) + "M"
  if (rate >= 1000) return (rate / 1000).toFixed(2) + "K"
  if (rate >= 1) return rate.toFixed(4)
  if (rate >= 0.0001) return rate.toFixed(6)
  if (rate >= 0.0000001) return rate.toFixed(10)
  // For extremely small numbers, use scientific notation
  return rate.toExponential(4)
}
